(function initializeMonciTheme() {
  const core = window.MonciCore;
  const wishlistKey = 'monci:wishlist:v1';

  function getWishlist() {
    try {
      return core.normalizeWishlist(JSON.parse(localStorage.getItem(wishlistKey) || '[]'));
    } catch {
      return [];
    }
  }

  function saveWishlist(handles) {
    const normalized = core.normalizeWishlist(handles);
    localStorage.setItem(wishlistKey, JSON.stringify(normalized));
    document.dispatchEvent(new CustomEvent('monci:wishlist-change', { detail: normalized }));
    return normalized;
  }

  function updateWishlistUi(handles = getWishlist()) {
    document.querySelectorAll('[data-wishlist-count]').forEach((counter) => {
      counter.textContent = handles.length;
      counter.hidden = handles.length === 0;
    });

    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const active = handles.includes(button.dataset.productHandle);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', button.dataset[active ? 'removeLabel' : 'addLabel'] || button.getAttribute('aria-label'));
    });
  }

  function toggleWishlist(handle) {
    const handles = getWishlist();
    const next = handles.includes(handle)
      ? handles.filter((item) => item !== handle)
      : [...handles, handle];
    return saveWishlist(next);
  }

  function openDialog(dialog) {
    if (!dialog || dialog.open) return;
    dialog.showModal();
  }

  function closeDialog(dialog) {
    if (dialog?.open) dialog.close();
  }

  function storefrontPath(pathname) {
    return `${window.Monci.root.replace(/\/?$/, '/')}${pathname.replace(/^\//, '')}`;
  }

  function bindDialogs() {
    const menuDialog = document.querySelector('[data-menu-dialog]');
    const searchDialog = document.querySelector('[data-search-dialog]');
    const cartDialog = document.querySelector('[data-cart-dialog]');

    document.querySelector('[data-menu-open]')?.addEventListener('click', () => openDialog(menuDialog));
    document.querySelector('[data-search-open]')?.addEventListener('click', () => {
      openDialog(searchDialog);
      searchDialog?.querySelector('input')?.focus();
    });
    document.querySelector('[data-cart-open]')?.addEventListener('click', () => openDialog(cartDialog));

    document.querySelectorAll('[data-dialog-close]').forEach((button) => {
      button.addEventListener('click', () => closeDialog(button.closest('dialog')));
    });

    document.querySelectorAll('dialog').forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog(dialog);
      });
    });
  }

  function bindHeader() {
    const header = document.querySelector('[data-site-header]');
    if (!header) return;

    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 44);
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  function bindLocalization() {
    document.querySelectorAll('[data-localization-select]').forEach((select) => {
      select.addEventListener('change', () => select.form?.submit());
    });
  }

  function bindCollectionSort() {
    document.querySelectorAll('[data-collection-sort]').forEach((select) => {
      select.addEventListener('change', () => select.form?.submit());
    });
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'hr-HR', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100);
  }

  function formatDecimalMoney(value) {
    return new Intl.NumberFormat(document.documentElement.lang || 'hr-HR', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number(value));
  }

  async function refreshCartDrawer(open = false, cart = null) {
    const response = await fetch(`${window.Monci.root}?section_id=header`);
    if (!response.ok) return false;
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const content = parsed.querySelector('[data-cart-drawer-content]');
    const count = parsed.querySelector('[data-cart-count]');

    if (content) {
      document.querySelector('[data-cart-drawer-content]')?.replaceWith(content);
    }
    document.querySelectorAll('[data-cart-count]').forEach((counter) => {
      counter.textContent = count?.textContent || '0';
      counter.hidden = Number(counter.textContent) === 0;
    });
    document.dispatchEvent(new CustomEvent('monci:cart-updated', { detail: { cart } }));
    if (open) openDialog(document.querySelector('[data-cart-dialog]'));
    return Boolean(content);
  }

  window.Monci.refreshCart = refreshCartDrawer;

  function reportCommerce(action) {
    document.dispatchEvent(new CustomEvent('monci:commerce', { detail: { action } }));
  }

  async function addToCart(form) {
    let response;
    try {
      response = await fetch(window.Monci.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
    } catch (error) {
      reportCommerce('add_to_cart_error');
      throw error;
    }

    if (!response.ok) {
      reportCommerce('add_to_cart_error');
      const error = await response.json();
      throw new Error(error.description || 'Unable to add item');
    }

    reportCommerce('add_to_cart_success');
    try {
      if (await refreshCartDrawer(true) === false) reportCommerce('cart_drawer_error');
    } catch (error) {
      reportCommerce('cart_drawer_error');
      throw error;
    }
  }

  async function changeCartLine(key, quantity) {
    const response = await fetch(window.Monci.cartChangeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!response.ok) throw new Error('Unable to update cart');
    await refreshCartDrawer(false);
  }

  function clampQuantity(quantity, minQuantity = 1, maxQuantity) {
    const min = Number.isFinite(Number(minQuantity)) ? Number(minQuantity) : 1;
    const max = maxQuantity === undefined || maxQuantity === null || maxQuantity === ''
      ? null
      : Number(maxQuantity);
    let nextQuantity = Math.max(min, Number(quantity) || min);
    if (Number.isFinite(max)) nextQuantity = Math.min(nextQuantity, max);
    return nextQuantity;
  }

  function bindCart() {
    const showSizeGuide = (dialog, show) => {
      const selection = dialog?.querySelector('[data-size-modal-selection]');
      const guide = dialog?.querySelector('[data-size-modal-guide]');
      if (!selection || !guide) return;
      if (show && guide.hidden) reportCommerce('size_guide_open');
      selection.hidden = show;
      guide.hidden = !show;
      const title = (show ? guide : selection).querySelector('h2');
      dialog.setAttribute('aria-labelledby', title.id);
      if (!show && selection.dataset.descriptionId) dialog.setAttribute('aria-describedby', selection.dataset.descriptionId);
      else dialog.removeAttribute('aria-describedby');
      guide.querySelector('details').open = show;
      if (dialog.open) {
        (show ? guide.querySelector('[data-size-guide-back]') : dialog.querySelector('[data-size-guide-open]'))?.focus({ preventScroll: true });
      }
    };

    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-product-form]');
      if (!form) return;
      event.preventDefault();
      reportCommerce('add_to_cart_attempt');
      const variantInput = form.querySelector('[name="id"]');
      if (form.hasAttribute('data-requires-option-selection') && !variantInput?.value) {
        reportCommerce('size_required');
        openDialog(form.closest('[data-product-root]')?.querySelector('[data-size-modal]'));
        return;
      }
      const button = form.querySelector('.product-form__submit, [type="submit"]');
      button?.setAttribute('aria-busy', 'true');
      try {
        await addToCart(form);
      } finally {
        button?.removeAttribute('aria-busy');
      }
    }, { capture: true });

    document.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-cart-change]');
      if (!button) return;
      event.preventDefault();
      if (button.disabled) return;
      const line = button.closest('[data-cart-line]');
      const currentQuantity = Number(line?.querySelector('.quantity span')?.textContent || 1);
      const nextQuantity = clampQuantity(button.dataset.quantity, button.dataset.minQuantity, button.dataset.maxQuantity);
      if (nextQuantity === currentQuantity) return;
      await changeCartLine(button.dataset.key, nextQuantity);
    });

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cart-quantity-step]');
      if (!button) return;
      event.preventDefault();
      if (button.disabled) return;
      const quantity = button.closest('.quantity');
      const input = quantity?.querySelector('input[name="updates[]"]');
      if (!input) return;
      const currentQuantity = Number(input.value || input.min || 1);
      const nextQuantity = clampQuantity(currentQuantity + Number(button.dataset.step || 0), input.min, input.max);
      if (nextQuantity === currentQuantity) return;
      input.value = String(nextQuantity);
      button.closest('form')?.requestSubmit();
    });

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-size-picker-open]');
      if (!button || button.disabled) return;
      event.preventDefault();
      const dialog = button.closest('[data-product-root]')?.querySelector('[data-size-modal]');
      showSizeGuide(dialog, false);
      openDialog(dialog);
    });

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-size-guide-open]');
      if (!button) return;
      event.preventDefault();
      showSizeGuide(button.closest('dialog'), true);
    });

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-size-guide-back]');
      if (!button) return;
      event.preventDefault();
      showSizeGuide(button.closest('dialog'), false);
    });

    document.addEventListener('click', (event) => {
      const optionButton = event.target.closest('[data-size-modal-option]');
      if (!optionButton || optionButton.disabled) return;
      event.preventDefault();
      const root = optionButton.closest('[data-product-root]');
      const optionGroup = [...root?.querySelectorAll('[data-option-group]') || []].find((group) => (
        [...group.querySelectorAll('input')].some((input) => input.name === `options[${optionButton.dataset.optionName}]`)
      ));
      const optionInput = [...optionGroup?.querySelectorAll('input') || []].find((input) => (
        input.value === optionButton.dataset.optionValue
      ));
      if (!optionInput) return;
      optionInput.checked = true;
      optionInput.dispatchEvent(new Event('change', { bubbles: true }));
      closeDialog(optionButton.closest('dialog'));
      const form = root.querySelector('[data-product-form]');
      if (form) form.requestSubmit();
    });
  }

  function bindWishlist() {
    updateWishlistUi();
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-wishlist-toggle]');
      if (!button) return;
      event.preventDefault();
      updateWishlistUi(toggleWishlist(button.dataset.productHandle));
    });
    document.addEventListener('monci:wishlist-change', (event) => updateWishlistUi(event.detail));
  }

  let searchAbortController;

  async function runPredictiveSearch(input, container) {
    const query = input.value.trim();
    if (query.length < 2) {
      container.innerHTML = '';
      return;
    }

    searchAbortController?.abort();
    searchAbortController = new AbortController();
    const url = `${window.Monci.predictiveSearchUrl}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`;
    const response = await fetch(url, {
      signal: searchAbortController.signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;
    const data = await response.json();
    const products = data.resources.results.products || [];
    container.innerHTML = products.map((product) => `
      <a href="${product.url}">
        ${product.image ? `<img src="${product.image}" alt="">` : ''}
        <span><strong>${product.title}</strong><br>${window.MonciPromo?.markup(Math.round(Number(product.price) * 100), product.type) || formatDecimalMoney(product.price)}</span>
      </a>
    `).join('');
  }

  function bindPredictiveSearch() {
    document.querySelectorAll('[data-predictive-search-input]').forEach((input) => {
      const container = input.closest('form')?.querySelector('[data-predictive-search]');
      input.addEventListener('input', () => runPredictiveSearch(input, container).catch(() => {}));
    });
  }

  function bindScrollSequences() {
    const sequences = document.querySelectorAll('[data-scroll-sequence]');
    if (!sequences.length) return;

    const update = () => {
      sequences.forEach((sequence) => {
        const count = Number(sequence.dataset.stepCount || sequence.querySelectorAll('[data-sequence-step]').length);
        const rect = sequence.getBoundingClientRect();
        const scrollable = Math.max(sequence.offsetHeight - window.innerHeight, 1);
        const progress = Math.min(Math.max(-rect.top / scrollable, 0), 1);
        const active = core.getScrollStep(progress, count);
        sequence.style.setProperty('--sequence-progress', progress);
        sequence.dataset.activeStep = active;
        sequence.querySelectorAll('[data-sequence-step]').forEach((step, index) => {
          const selected = index === active;
          step.classList.toggle('is-active', selected);
          step.setAttribute('aria-hidden', String(!selected));
        });
        sequence.querySelectorAll('[data-sequence-nav]').forEach((item, index) => {
          item.classList.toggle('is-active', index === active);
        });
      });
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  }

  function bindProductVariants() {
    document.querySelectorAll('[data-product-root]').forEach((root) => {
      const variants = JSON.parse(root.querySelector('[data-variants-json]')?.textContent || '[]');
      const form = root.querySelector('[data-product-form]');
      const variantId = form?.querySelector('[name="id"]');
      const submit = form?.querySelector('.product-form__submit, [type="submit"]');
      const sizePicker = submit?.hasAttribute('data-size-picker-open');
      if (sizePicker) {
        submit.type = 'button';
        form.classList.add('product-form--size-picker');
      }
      const price = root.querySelector('[data-product-price]');
      const compare = root.querySelector('[data-product-compare]');
      const discount = root.querySelector('[data-product-discount]');
      const saleRow = root.querySelector('[data-product-sale-row]');
      const lowest = root.querySelector('[data-product-lowest]');

      const update = () => {
        const options = [...root.querySelectorAll('[data-option-group]')].map((group) => (
          group.querySelector('input:checked, select')?.value
        ));
        const hasCompleteSelection = options.every(Boolean);
        const variant = hasCompleteSelection ? core.resolveVariant(variants, options) : null;
        if (variantId) variantId.value = variant?.id || '';
        if (submit) {
          if (sizePicker) {
            submit.disabled = !variants.some((entry) => entry.available);
            submit.textContent = submit.disabled ? submit.dataset.soldLabel : submit.dataset.chooseLabel;
          } else {
            submit.disabled = hasCompleteSelection && !variant?.available;
            submit.textContent = !hasCompleteSelection || variant?.available
              ? submit.dataset.addLabel
              : submit.dataset.soldLabel;
          }
        }
        if (sizePicker) {
          const groups = [...root.querySelectorAll('[data-option-group]')];
          root.querySelectorAll('[data-size-modal-option]').forEach((button) => {
            const optionIndex = groups.findIndex((group) => (
              [...group.querySelectorAll('input')].some((input) => input.name === `options[${button.dataset.optionName}]`)
            ));
            button.disabled = !variants.some((entry) => entry.available && entry.options.every((value, index) => (
              index === optionIndex ? value === button.dataset.optionValue : !options[index] || value === options[index]
            )));
            button.setAttribute('aria-disabled', String(button.disabled));
          });
        }
        if (variant) {
          const compareAt = Number(variant.compare_at_price || 0);
          const currentPrice = Number(variant.price || 0);
          const isDiscounted = compareAt > currentPrice;
          if (price) price.textContent = formatMoney(currentPrice);
          if (saleRow) saleRow.hidden = !isDiscounted;
          if (compare) {
            compare.textContent = isDiscounted ? formatMoney(compareAt) : '';
            compare.hidden = !isDiscounted;
          }
          if (discount) {
            const percent = isDiscounted ? Math.round(((compareAt - currentPrice) / compareAt) * 100) : 0;
            discount.textContent = isDiscounted ? `-${percent}%` : '';
            discount.hidden = !isDiscounted;
          }
          if (lowest) {
            const lowestValue = lowest.querySelector('strong');
            const lowestPrice = currentPrice;
            const showLowest = isDiscounted;
            if (lowestValue) lowestValue.textContent = showLowest ? formatMoney(lowestPrice) : '';
            lowest.hidden = !showLowest;
          }
        }
        if (variant) root.dispatchEvent(new CustomEvent('monci:price-updated', { bubbles: true, detail: { price: Number(variant.price) } }));
        if (variant?.featured_media) {
          root.querySelector(`[data-media-id="${variant.featured_media.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      };

      root.querySelectorAll('[data-option-group] input, [data-option-group] select').forEach((input) => {
        input.addEventListener('change', update);
      });
      update();
    });
  }

  function bindProductGallery() {
    document.querySelectorAll('[data-product-root]').forEach((root) => {
      const dialog = root.querySelector('[data-product-gallery-dialog]');
      if (!dialog) return;

      const openers = [...root.querySelectorAll('[data-product-gallery-open]')];
      const thumbnails = [...dialog.querySelectorAll('[data-product-gallery-thumbnail]')];
      const image = dialog.querySelector('[data-product-gallery-image]');
      const canvas = dialog.querySelector('[data-product-gallery-canvas]');
      const current = dialog.querySelector('[data-product-gallery-current]');
      const count = dialog.querySelector('[data-product-gallery-count]');
      const hint = dialog.querySelector('[data-product-gallery-hint]');
      const activePointers = new Map();
      let activeIndex = 0;
      let zoom = 1;
      let panX = 0;
      let panY = 0;
      let gestureState = null;
      let hintTimer = null;
      let lastPointerType = 'mouse';
      let lastOpener = null;
      let lastTap = null;

      if (!thumbnails.length || !image || !canvas) return;

      const getCanvasPoint = (point) => {
        const rect = canvas.getBoundingClientRect();
        return {
          x: Number(point.x ?? point.clientX) - (rect.left + (rect.width / 2)),
          y: Number(point.y ?? point.clientY) - (rect.top + (rect.height / 2)),
        };
      };

      const hideHint = () => {
        if (hintTimer) window.clearTimeout(hintTimer);
        hintTimer = null;
        hint?.classList.remove('is-visible');
      };

      const showHint = () => {
        if (!hint) return;
        hideHint();
        const isTouchInput = window.matchMedia('(pointer: coarse)').matches;
        hint.textContent = isTouchInput ? hint.dataset.touchCopy : hint.dataset.pointerCopy;
        hint.classList.add('is-visible');
        hintTimer = window.setTimeout(hideHint, 2400);
      };

      const releasePointer = (pointerId) => {
        if (!canvas.hasPointerCapture?.(pointerId)) return;
        try {
          canvas.releasePointerCapture(pointerId);
        } catch (_error) {
          // The browser can release capture before pointer cleanup runs.
        }
      };

      const clearGesture = () => {
        const pointerIds = [...activePointers.keys()];
        activePointers.clear();
        gestureState = null;
        canvas.classList.remove('is-dragging', 'is-gesturing');
        pointerIds.forEach(releasePointer);
      };

      const panBounds = () => ({
        x: Math.max(((image.clientWidth * zoom) - canvas.clientWidth) / 2, 0),
        y: Math.max(((image.clientHeight * zoom) - canvas.clientHeight) / 2, 0),
      });

      const applyTransform = () => {
        const bounds = panBounds();
        panX = Math.min(Math.max(panX, -bounds.x), bounds.x);
        panY = Math.min(Math.max(panY, -bounds.y), bounds.y);
        image.style.transform = `translate3d(${panX}px, ${panY}px, 0) scale(${zoom})`;
        canvas.classList.toggle('is-zoomed', zoom > 1);
      };

      const setZoomAt = (value, focal = { x: 0, y: 0 }, nextFocal = focal, sourceState = null) => {
        const targetZoom = core.clampGalleryZoom(value);
        const origin = sourceState || { zoom, panX, panY };
        const nextPan = core.getGalleryPanForZoom({
          panX: origin.panX,
          panY: origin.panY,
          currentZoom: origin.zoom,
          targetZoom,
          focalX: focal.x,
          focalY: focal.y,
          nextFocalX: nextFocal.x,
          nextFocalY: nextFocal.y,
        });

        if (targetZoom !== zoom || nextPan.x !== panX || nextPan.y !== panY) hideHint();
        zoom = targetZoom;
        panX = nextPan.x;
        panY = nextPan.y;
        if (targetZoom === 1) {
          panX = 0;
          panY = 0;
        }
        applyTransform();
      };

      const setZoom = (value) => setZoomAt(value, { x: 0, y: 0 });

      const selectImage = (index) => {
        clearGesture();
        hideHint();
        lastTap = null;
        const nextIndex = Math.min(Math.max(Number(index) || 0, 0), thumbnails.length - 1);
        const thumbnail = thumbnails[nextIndex];
        activeIndex = nextIndex;
        image.src = thumbnail.dataset.src;
        image.srcset = thumbnail.dataset.srcset || '';
        image.alt = thumbnail.dataset.alt || '';
        if (thumbnail.dataset.width) image.setAttribute('width', thumbnail.dataset.width);
        if (thumbnail.dataset.height) image.setAttribute('height', thumbnail.dataset.height);
        thumbnails.forEach((item, itemIndex) => {
          if (itemIndex === activeIndex) item.setAttribute('aria-current', 'true');
          else item.removeAttribute('aria-current');
        });
        thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        if (current) current.textContent = String(activeIndex + 1);
        if (count?.dataset.countLabel) {
          count.setAttribute('aria-label', count.dataset.countLabel.replace('__CURRENT__', String(activeIndex + 1)));
        }
        setZoom(1);
      };

      const navigate = (delta) => {
        selectImage(core.getLoopedGalleryIndex(activeIndex, delta, thumbnails.length));
      };

      openers.forEach((opener) => {
        opener.addEventListener('click', () => {
          lastOpener = opener;
          selectImage(opener.dataset.galleryIndex);
          dialog.showModal();
          document.body.classList.add('product-gallery-viewer-open');
          showHint();
          requestAnimationFrame(() => canvas.focus({ preventScroll: true }));
        });
      });

      thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => selectImage(thumbnail.dataset.galleryIndex));
      });

      dialog.querySelector('[data-product-gallery-close]')?.addEventListener('click', () => dialog.close());
      dialog.querySelector('[data-product-gallery-previous]')?.addEventListener('click', () => navigate(-1));
      dialog.querySelector('[data-product-gallery-next]')?.addEventListener('click', () => navigate(1));

      canvas.addEventListener('dblclick', (event) => {
        if (lastPointerType === 'touch') return;
        setZoomAt(zoom === 1 ? 2 : 1, getCanvasPoint(event));
      });
      canvas.addEventListener('wheel', (event) => {
        event.preventDefault();
        lastPointerType = 'mouse';
        setZoomAt(zoom + (event.deltaY < 0 ? 0.25 : -0.25), getCanvasPoint(event));
      }, { passive: false });

      canvas.addEventListener('pointerdown', (event) => {
        lastPointerType = event.pointerType;
        hideHint();
        activePointers.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY,
          startX: event.clientX,
          startY: event.clientY,
          pointerType: event.pointerType,
          moved: false,
        });
        canvas.setPointerCapture(event.pointerId);

        const touchPointers = [...activePointers.entries()].filter(([, pointer]) => pointer.pointerType === 'touch');
        if (touchPointers.length >= 2) {
          const [first, second] = touchPointers;
          const midpoint = core.getPointMidpoint(first[1], second[1]);
          gestureState = {
            type: 'pinch',
            pointerIds: [first[0], second[0]],
            distance: Math.max(core.getPointDistance(first[1], second[1]), 1),
            midpoint: getCanvasPoint(midpoint),
            zoom,
            panX,
            panY,
          };
          lastTap = null;
          canvas.classList.remove('is-dragging');
          canvas.classList.add('is-gesturing');
        } else if (zoom > 1) {
          gestureState = {
            type: 'drag',
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            panX,
            panY,
          };
          canvas.classList.add('is-dragging', 'is-gesturing');
        }
      });

      canvas.addEventListener('pointermove', (event) => {
        const pointer = activePointers.get(event.pointerId);
        if (!pointer) return;
        pointer.x = event.clientX;
        pointer.y = event.clientY;
        pointer.moved = pointer.moved || Math.hypot(
          event.clientX - pointer.startX,
          event.clientY - pointer.startY,
        ) > 6;

        if (gestureState?.type === 'pinch') {
          const first = activePointers.get(gestureState.pointerIds[0]);
          const second = activePointers.get(gestureState.pointerIds[1]);
          if (!first || !second) return;
          const distance = Math.max(core.getPointDistance(first, second), 1);
          const midpoint = getCanvasPoint(core.getPointMidpoint(first, second));
          const targetZoom = gestureState.zoom * (distance / gestureState.distance);
          setZoomAt(targetZoom, gestureState.midpoint, midpoint, gestureState);
          return;
        }

        if (gestureState?.type !== 'drag' || gestureState.pointerId !== event.pointerId) return;
        panX = gestureState.panX + (event.clientX - gestureState.startX);
        panY = gestureState.panY + (event.clientY - gestureState.startY);
        hideHint();
        applyTransform();
      });

      canvas.addEventListener('pointerup', (event) => {
        const pointer = activePointers.get(event.pointerId);
        const wasPinch = gestureState?.type === 'pinch';
        activePointers.delete(event.pointerId);
        releasePointer(event.pointerId);

        if (wasPinch) {
          clearGesture();
          lastTap = null;
          return;
        }

        if (gestureState?.pointerId === event.pointerId) {
          gestureState = null;
          canvas.classList.remove('is-dragging', 'is-gesturing');
        }

        if (event.pointerType !== 'touch' || pointer?.moved) return;
        const point = getCanvasPoint(event);
        const tap = { time: event.timeStamp, x: point.x, y: point.y };
        if (core.isGalleryDoubleTap(lastTap, tap)) {
          setZoomAt(zoom === 1 ? 2 : 1, point);
          lastTap = null;
        } else {
          lastTap = tap;
        }
      });

      const cancelPointer = (event) => {
        if (!activePointers.has(event.pointerId)) return;
        activePointers.delete(event.pointerId);
        clearGesture();
        lastTap = null;
      };

      canvas.addEventListener('pointercancel', cancelPointer);
      canvas.addEventListener('lostpointercapture', cancelPointer);

      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          navigate(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          navigate(1);
        } else if (event.key === '+' || event.key === '=') {
          event.preventDefault();
          setZoom(zoom + 0.25);
        } else if (event.key === '-' || event.key === '_') {
          event.preventDefault();
          setZoom(zoom - 0.25);
        }
      });

      dialog.addEventListener('close', () => {
        document.body.classList.remove('product-gallery-viewer-open');
        clearGesture();
        hideHint();
        setZoom(1);
        lastTap = null;
        requestAnimationFrame(() => lastOpener?.focus());
      });
      image.addEventListener('load', applyTransform);
      window.addEventListener('resize', applyTransform);
      setZoom(1);
    });
  }

  function bindWishlistPage() {
    const container = document.querySelector('[data-wishlist-products]');
    if (!container) return;
    const handles = getWishlist();
    if (!handles.length) return;
    document.querySelector('[data-wishlist-empty]')?.setAttribute('hidden', '');
    const removeLabel = container.dataset.removeLabel || 'Remove';

    Promise.all(handles.map(async (handle) => {
      const response = await fetch(storefrontPath(`products/${handle}.js`));
      return response.ok ? response.json() : null;
    })).then((products) => {
      container.innerHTML = products.filter(Boolean).map((product) => `
        <article class="wishlist-item">
          <a href="${product.url}">
            ${product.featured_image ? `<img src="${product.featured_image}" alt="">` : ''}
            <h3>${product.title}</h3>
            ${window.MonciPromo?.markup(Number(product.price), product.type) || `<span>${formatMoney(product.price)}</span>`}
          </a>
          <button class="button" type="button" data-wishlist-toggle data-product-handle="${product.handle}">${removeLabel}</button>
        </article>
      `).join('');
      updateWishlistUi();
    });
  }

  bindDialogs();
  bindHeader();
  bindLocalization();
  bindCollectionSort();
  bindCart();
  bindWishlist();
  bindPredictiveSearch();
  bindScrollSequences();
  bindProductVariants();
  bindProductGallery();
  bindWishlistPage();
})();

(function bindMonciCookiePreferences() {
  const showPreferences = () => window.privacyBanner.showPreferences().catch((error) => {
    console.error('Unable to open cookie preferences.', error);
  });

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || !(event.target instanceof Element)) return;
    const link = event.target.closest('a[href$="#shopifyReshowConsentBanner"]');
    if (!link) return;
    event.preventDefault();

    if (window.privacyBanner?.showPreferences) {
      showPreferences();
    } else {
      document.querySelector('script[src*="/privacy-banner/storefront-banner.js"]')
        ?.addEventListener('load', showPreferences, { once: true });
    }
  });
})();
